/*
 * Copyright (C) 2026 - Potentially Ltd
 *
 * Please see distribution for license.
 */

import { Injectable } from '@angular/core';
import { CertificateHelper } from '@app/app/shared/helpers/certificate.helper';
import { PrintPageHelper } from '@app/app/shared/helpers/print-page/print-page.helper';
import {
  Certificate,
  CertificateAccentColor,
  CertificateFont,
  CertificateOrientation,
  CertificateRequest,
  CertificateTemplate,
  PublicUserCertificateDetails,
  UserCertificateDetails,
} from '@app/app/shared/models/certificate/models/certificate.model';
import { ObservableResult } from '@app/app/shared/store';
import { Observable, defer } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';
import { CertificateDataService } from './certificate-data.service';

const STORAGE_KEY = 'ptl_mock_certificates';

/** Landscape/portrait canvas size shared by every preview thumbnail — mirrors the aspect ratio of the PDF templates rendered by pdf-render-microservice. */
function previewCanvasSize(orientation: CertificateOrientation): { w: number; h: number } {
  return orientation === 'PORTRAIT' ? { w: 226, h: 320 } : { w: 320, h: 226 };
}

// Mirrors template-classic-*.html: cream sheet, double navy rule border, centered serif eyebrow + italic name.
function classicPreviewSvg(orientation: CertificateOrientation): string {
  const { w, h } = previewCanvasSize(orientation);
  const cx = w / 2;
  const cy = h / 2;
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}" viewBox="0 0 ${w} ${h}">
      <rect width="${w}" height="${h}" fill="#faf6ea" />
      <rect x="8" y="8" width="${w - 16}" height="${h - 16}" fill="none" stroke="#1f3a5f" stroke-width="2" />
      <rect x="13" y="13" width="${w - 26}" height="${h - 26}" fill="none" stroke="#1f3a5f" stroke-width="1" />
      <text x="${cx}" y="${cy - 18}" text-anchor="middle" font-family="Georgia, 'Times New Roman', serif" font-size="7.5" letter-spacing="2.4" fill="#1f3a5f">CERTIFICATE OF COMPLETION</text>
      <line x1="${cx - 20}" y1="${cy - 8}" x2="${cx + 20}" y2="${cy - 8}" stroke="#1f3a5f" stroke-width="1" />
      <text x="${cx}" y="${cy + 18}" text-anchor="middle" font-family="Georgia, 'Times New Roman', serif" font-style="italic" font-size="17" fill="#2c2416">Learner Name</text>
      <text x="${cx}" y="${cy + 36}" text-anchor="middle" font-family="Georgia, 'Times New Roman', serif" font-size="9" fill="#8a8578">Program title</text>
      <line x1="${cx - 35}" y1="${h - 22}" x2="${cx + 35}" y2="${h - 22}" stroke="#2c2416" stroke-opacity="0.2" stroke-width="1" />
    </svg>`.trim();
  return `data:image/svg+xml,${encodeURIComponent(svg)}`;
}

// Mirrors template-modern-*.html: white sheet, full-height accent bar on the leading edge, left-aligned sans-serif content.
function modernPreviewSvg(orientation: CertificateOrientation): string {
  const { w, h } = previewCanvasSize(orientation);
  const contentTop = h * 0.34;
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}" viewBox="0 0 ${w} ${h}">
      <rect width="${w}" height="${h}" fill="#ffffff" />
      <rect width="7" height="${h}" fill="#1f3a5f" />
      <text x="24" y="${contentTop}" font-family="'Helvetica Neue', Arial, sans-serif" font-size="7.5" letter-spacing="1.6" fill="#1f3a5f">CERTIFICATE OF COMPLETION</text>
      <text x="24" y="${contentTop + 26}" font-family="'Helvetica Neue', Arial, sans-serif" font-size="20" font-weight="700" fill="#14181f">Learner Name</text>
      <text x="24" y="${contentTop + 50}" font-family="'Helvetica Neue', Arial, sans-serif" font-size="10" fill="#14181f">Program title</text>
      <line x1="24" y1="${h - 26}" x2="94" y2="${h - 26}" stroke="#14181f" stroke-opacity="0.25" stroke-width="1" />
    </svg>`.trim();
  return `data:image/svg+xml,${encodeURIComponent(svg)}`;
}

// Mirrors template-academic-*.html: off-white sheet framed top and bottom by a solid accent band, serif "conferred" copy.
function academicPreviewSvg(orientation: CertificateOrientation): string {
  const { w, h } = previewCanvasSize(orientation);
  const cx = w / 2;
  const mastheadHeight = orientation === 'PORTRAIT' ? 46 : 38;
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}" viewBox="0 0 ${w} ${h}">
      <rect width="${w}" height="${h}" fill="#fbfaf7" />
      <rect width="${w}" height="${mastheadHeight}" fill="#1f3a5f" />
      <text x="${cx}" y="${mastheadHeight / 2 + 3}" text-anchor="middle" font-family="'Palatino Linotype', Georgia, serif" font-size="7.5" letter-spacing="2" fill="#ffffff">CERTIFICATE OF ACHIEVEMENT</text>
      <text x="${cx}" y="${mastheadHeight + 26}" text-anchor="middle" font-family="Georgia, serif" font-size="7" letter-spacing="1.4" fill="#9a9384">THIS IS TO CERTIFY THAT</text>
      <text x="${cx}" y="${mastheadHeight + 48}" text-anchor="middle" font-family="'Palatino Linotype', Georgia, serif" font-size="15" fill="#241f18">Learner Name</text>
      <line x1="${cx - 32}" y1="${mastheadHeight + 60}" x2="${cx + 32}" y2="${mastheadHeight + 60}" stroke="#000000" stroke-opacity="0.16" />
      <text x="${cx}" y="${mastheadHeight + 78}" text-anchor="middle" font-family="Georgia, serif" font-size="9" font-style="italic" fill="#241f18">Program title</text>
      <line x1="${cx - 35}" y1="${h - 22}" x2="${cx + 35}" y2="${h - 22}" stroke="#241f18" stroke-opacity="0.18" stroke-width="1" />
      <rect y="${h - 6}" width="${w}" height="6" fill="#1f3a5f" />
    </svg>`.trim();
  return `data:image/svg+xml,${encodeURIComponent(svg)}`;
}

// Mirrors template-achievement-*.html: white sheet, full-width accent banner near the top, bold sans-serif name.
function achievementPreviewSvg(orientation: CertificateOrientation): string {
  const { w, h } = previewCanvasSize(orientation);
  const cx = w / 2;
  const bandY = orientation === 'PORTRAIT' ? 40 : 32;
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}" viewBox="0 0 ${w} ${h}">
      <rect width="${w}" height="${h}" fill="#ffffff" />
      <rect x="0" y="${bandY}" width="${w}" height="16" fill="#1f3a5f" />
      <text x="${cx}" y="${bandY + 11}" text-anchor="middle" font-family="'Helvetica Neue', Arial, sans-serif" font-size="7" letter-spacing="2" fill="#ffffff">CERTIFICATE OF ACHIEVEMENT</text>
      <text x="${cx}" y="${bandY + 44}" text-anchor="middle" font-family="'Helvetica Neue', Arial, sans-serif" font-size="17" font-weight="700" fill="#191713">Learner Name</text>
      <text x="${cx}" y="${bandY + 64}" text-anchor="middle" font-family="'Helvetica Neue', Arial, sans-serif" font-size="9" fill="#191713">Program title</text>
      <line x1="${cx - 35}" y1="${h - 22}" x2="${cx + 35}" y2="${h - 22}" stroke="#191713" stroke-opacity="0.2" stroke-width="1" />
    </svg>`.trim();
  return `data:image/svg+xml,${encodeURIComponent(svg)}`;
}

/**
 * The 4-color accent palette shared by every template — kept print-safe and legible against both the
 * cream (Classic) and white (Modern/Academic/Achievement) sheet backgrounds.
 */
const ACCENT_NAVY: CertificateAccentColor = { id: 'navy', label: 'Navy', hex: '#1f3a5f' };
const ACCENT_BURGUNDY: CertificateAccentColor = { id: 'burgundy', label: 'Burgundy', hex: '#5a2a3b' };
const ACCENT_FOREST: CertificateAccentColor = { id: 'forest', label: 'Forest', hex: '#1e4b44' };
const ACCENT_GRAPHITE: CertificateAccentColor = { id: 'graphite', label: 'Graphite', hex: '#3b3550' };

const FONT_GEORGIA: CertificateFont = { id: 'georgia', label: 'Georgia', fontFamily: "Georgia, 'Times New Roman', serif" };
const FONT_PALATINO: CertificateFont = {
  id: 'palatino',
  label: 'Palatino',
  fontFamily: "'Palatino Linotype', Palatino, 'Book Antiqua', Georgia, serif",
};
const FONT_PLAYFAIR: CertificateFont = { id: 'playfair', label: 'Playfair Display', fontFamily: "'Playfair Display', Georgia, serif" };
const FONT_GARAMOND: CertificateFont = { id: 'garamond', label: 'EB Garamond', fontFamily: "'EB Garamond', Georgia, serif" };
const FONT_HELVETICA: CertificateFont = {
  id: 'helvetica',
  label: 'Helvetica Neue',
  fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif",
};
const FONT_POPPINS: CertificateFont = { id: 'poppins', label: 'Poppins', fontFamily: "'Poppins', 'Helvetica Neue', Arial, sans-serif" };
const FONT_INTER: CertificateFont = { id: 'inter', label: 'Inter', fontFamily: "Inter, 'Helvetica Neue', Arial, sans-serif" };

const ALL_FONTS: CertificateFont[] = [FONT_GEORGIA, FONT_PALATINO, FONT_PLAYFAIR, FONT_GARAMOND, FONT_HELVETICA, FONT_POPPINS, FONT_INTER];

/** Every field slot pdf-render-microservice's certificate templates support — shared by all 8 designs. */
const ALL_FIELD_SLOTS: CertificateTemplate['fieldSlots'] = [
  'LEARNER_NAME',
  'PROGRAM_TITLE',
  'SIGNATORY_NAME',
  'SIGNATORY_ROLE',
  'SIGNATORY_2_NAME',
  'SIGNATORY_2_ROLE',
  'COMPLETION_DATE',
  'INSTITUTION_LOGO',
  'BADGE_IMAGE',
  'VERIFICATION_QR',
];

/** 4 designs × 2 orientations, mirroring the 8 templates in pdf-render-microservice's templates/certificate directory. */
const SEEDED_TEMPLATES: CertificateTemplate[] = [
  {
    uid: 'template-classic-landscape',
    name: 'Classic',
    orientation: 'LANDSCAPE',
    previewImageUrl: classicPreviewSvg('LANDSCAPE'),
    category: 'Classic',
    tags: ['navy', 'serif', 'traditional'],
    // Navy leads — the institutional, safe default (also what the template previews ship with).
    accentColors: [ACCENT_NAVY, ACCENT_BURGUNDY, ACCENT_FOREST, ACCENT_GRAPHITE],
    fonts: ALL_FONTS,
    defaultFontId: 'georgia',
    fieldSlots: ALL_FIELD_SLOTS,
  },
  {
    uid: 'template-classic-portrait',
    name: 'Classic',
    orientation: 'PORTRAIT',
    previewImageUrl: classicPreviewSvg('PORTRAIT'),
    category: 'Classic',
    tags: ['navy', 'serif', 'traditional'],
    // Navy leads — the institutional, safe default (also what the template previews ship with).
    accentColors: [ACCENT_NAVY, ACCENT_BURGUNDY, ACCENT_FOREST, ACCENT_GRAPHITE],
    fonts: ALL_FONTS,
    defaultFontId: 'georgia',
    fieldSlots: ALL_FIELD_SLOTS,
  },
  {
    uid: 'template-modern-landscape',
    name: 'Modern',
    orientation: 'LANDSCAPE',
    previewImageUrl: modernPreviewSvg('LANDSCAPE'),
    category: 'Modern',
    tags: ['minimal', 'sans-serif', 'bold'],
    // Navy leads — the institutional, safe default (also what the template previews ship with).
    accentColors: [ACCENT_NAVY, ACCENT_GRAPHITE, ACCENT_BURGUNDY, ACCENT_FOREST],
    fonts: ALL_FONTS,
    defaultFontId: 'helvetica',
    fieldSlots: ALL_FIELD_SLOTS,
  },
  {
    uid: 'template-modern-portrait',
    name: 'Modern',
    orientation: 'PORTRAIT',
    previewImageUrl: modernPreviewSvg('PORTRAIT'),
    category: 'Modern',
    tags: ['minimal', 'sans-serif', 'bold'],
    // Navy leads — the institutional, safe default (also what the template previews ship with).
    accentColors: [ACCENT_NAVY, ACCENT_GRAPHITE, ACCENT_BURGUNDY, ACCENT_FOREST],
    fonts: ALL_FONTS,
    defaultFontId: 'helvetica',
    fieldSlots: ALL_FIELD_SLOTS,
  },
  {
    uid: 'template-academic-landscape',
    name: 'Academic',
    orientation: 'LANDSCAPE',
    previewImageUrl: academicPreviewSvg('LANDSCAPE'),
    category: 'Academic',
    tags: ['serif', 'formal', 'banner'],
    // Navy leads — the institutional, safe default (also what the template previews ship with).
    accentColors: [ACCENT_NAVY, ACCENT_FOREST, ACCENT_BURGUNDY, ACCENT_GRAPHITE],
    fonts: ALL_FONTS,
    defaultFontId: 'palatino',
    fieldSlots: ALL_FIELD_SLOTS,
  },
  {
    uid: 'template-academic-portrait',
    name: 'Academic',
    orientation: 'PORTRAIT',
    previewImageUrl: academicPreviewSvg('PORTRAIT'),
    category: 'Academic',
    tags: ['serif', 'formal', 'banner'],
    // Navy leads — the institutional, safe default (also what the template previews ship with).
    accentColors: [ACCENT_NAVY, ACCENT_FOREST, ACCENT_BURGUNDY, ACCENT_GRAPHITE],
    fonts: ALL_FONTS,
    defaultFontId: 'palatino',
    fieldSlots: ALL_FIELD_SLOTS,
  },
  {
    uid: 'template-achievement-landscape',
    name: 'Achievement',
    orientation: 'LANDSCAPE',
    previewImageUrl: achievementPreviewSvg('LANDSCAPE'),
    category: 'Achievement',
    tags: ['bold', 'sans-serif', 'banner'],
    // Navy leads — the institutional, safe default (also what the template previews ship with).
    accentColors: [ACCENT_NAVY, ACCENT_BURGUNDY, ACCENT_FOREST, ACCENT_GRAPHITE],
    fonts: ALL_FONTS,
    defaultFontId: 'helvetica',
    fieldSlots: ALL_FIELD_SLOTS,
  },
  {
    uid: 'template-achievement-portrait',
    name: 'Achievement',
    orientation: 'PORTRAIT',
    previewImageUrl: achievementPreviewSvg('PORTRAIT'),
    category: 'Achievement',
    tags: ['bold', 'sans-serif', 'banner'],
    // Navy leads — the institutional, safe default (also what the template previews ship with).
    accentColors: [ACCENT_NAVY, ACCENT_BURGUNDY, ACCENT_FOREST, ACCENT_GRAPHITE],
    fonts: ALL_FONTS,
    defaultFontId: 'helvetica',
    fieldSlots: ALL_FIELD_SLOTS,
  },
];

const SEEDED_ISSUED_CERTIFICATES: UserCertificateDetails[] = [
  {
    uid: 'issued-cert-001',
    certificateId: 'template-classic-landscape',
    resourceId: 'resource-001',
    title: 'Advanced Data Analytics Playlist',
    issuingOrganization: 'Potential.ly',
    completionDate: '2026-06-15',
    issuedAt: '2026-06-15T10:00:00',
    verification: { qrLinksTo: 'https://verify.yuna.dev/c/issued-cert-001' },
    openBadgeCredentialUrl: 'https://api.yuna.dev/certificates/public/issued-cert-001/credential',
  },
  {
    uid: 'issued-cert-002',
    certificateId: 'template-modern-landscape',
    resourceId: 'resource-002',
    title: 'Leadership Fundamentals',
    issuingOrganization: 'Potential.ly',
    completionDate: '2025-02-10',
    issuedAt: '2025-02-10T09:30:00',
    expiresOn: '2027-02-10',
    verification: { qrLinksTo: 'https://verify.yuna.dev/c/issued-cert-002' },
    openBadgeCredentialUrl: 'https://api.yuna.dev/certificates/public/issued-cert-002/credential',
  },
];

/**
 * Mock implementation only — the certificate backend is a separate parallel track (YUNA-1602).
 * State is kept in localStorage (falling back to an in-memory map) so a certificate survives
 * navigating away and back during a demo walkthrough.
 */
@Injectable()
export class MockCertificateDataService implements CertificateDataService {
  private readonly store: Record<string, Certificate> = this.loadStore();

  getTemplates(): ObservableResult<CertificateTemplate[]> {
    return ObservableResult.ofSuccess(SEEDED_TEMPLATES);
  }

  getCertificate(resourceId: string): ObservableResult<Certificate | undefined> {
    return ObservableResult.ofSuccess(this.store[resourceId]);
  }

  saveCertificate(resourceId: string, request: CertificateRequest): ObservableResult<Certificate> {
    const existing = this.store[resourceId];
    const certificate: Certificate = {
      uid: existing?.uid ?? uuidv4(),
      resourceId,
      title: request.title ?? existing?.title ?? '',
      completionText: request.completionText ?? existing?.completionText,
      status: existing?.status ?? 'INACTIVE',
      source: 'TEMPLATE',
      templateId: request.templateId ?? existing?.templateId,
      signatory: request.signatory ?? existing?.signatory ?? { name: '', role: '' },
      secondSignatory: request.secondSignatory ?? existing?.secondSignatory,
      institutionLogoUrl: request.institutionLogoUrl ?? existing?.institutionLogoUrl,
      badgeImageUrl: request.badgeImageUrl ?? existing?.badgeImageUrl,
      accentColorId: request.accentColorId ?? existing?.accentColorId,
      fontId: request.fontId ?? existing?.fontId,
      verification: existing?.verification ?? { qrLinksTo: `https://verify.yuna.dev/c/${uuidv4()}` },
      expiration: request.expiration ?? existing?.expiration ?? { type: 'NEVER' },
    };
    this.store[resourceId] = certificate;
    this.persistStore();
    return ObservableResult.ofSuccess(certificate);
  }

  updateCertificateActiveStatus(resourceId: string, status: boolean): ObservableResult<Certificate> {
    const existing = this.store[resourceId];
    if (!existing) {
      return ObservableResult.ofError('No certificate to activate yet');
    }
    existing.status = status ? 'ACTIVE' : 'INACTIVE';
    this.persistStore();
    return ObservableResult.ofSuccess(existing);
  }

  downloadSamplePdf(_resourceId: string, _request: CertificateRequest, previewElements?: NodeListOf<Element>): Observable<Blob> {
    return defer(async () => {
      const doc = await PrintPageHelper.addPages(previewElements ?? ([] as unknown as NodeListOf<Element>));
      return doc.output('blob');
    });
  }

  getIssuedCertificates(): ObservableResult<UserCertificateDetails[]> {
    return ObservableResult.ofSuccess(SEEDED_ISSUED_CERTIFICATES);
  }

  downloadIssuedPdf(_resourceId: string): Observable<Blob> {
    return defer(async () => {
      const doc = await PrintPageHelper.addPages([] as unknown as NodeListOf<Element>);
      return doc.output('blob');
    });
  }

  getCertificateVerification(userCertificateUid: string): ObservableResult<PublicUserCertificateDetails | undefined> {
    const issued = SEEDED_ISSUED_CERTIFICATES.find((certificate) => certificate.uid === userCertificateUid);
    if (!issued) {
      return ObservableResult.ofSuccess(undefined);
    }
    return ObservableResult.ofSuccess({
      uid: issued.uid,
      learnerName: 'Learner Name',
      title: issued.title,
      issuingOrganization: 'Potentially Ltd',
      completionDate: issued.completionDate,
      expiresOn: issued.expiresOn,
      expired: CertificateHelper.isExpired(issued.expiresOn),
    });
  }

  private loadStore(): Record<string, Certificate> {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? (JSON.parse(raw) as Record<string, Certificate>) : {};
    } catch {
      return {};
    }
  }

  private persistStore(): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.store));
    } catch {
      // localStorage unavailable (e.g. private browsing) — state still survives for the current session.
    }
  }
}
